import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardHeader from "@/components/DashboardHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  HeartPulse,
  Loader2,
  Settings2,
  Shield,
  Sparkles,
  UserRound,
  Users,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 20 — Parent dashboard (Block C).
 *
 * Five tabs:
 *   - Overview           — headline metrics + linked children
 *   - Children           — per-child summary (progress + activity)
 *   - Pending Approvals  — full drawer with Approve / Reject
 *   - Progress           — aggregated progress across children
 *   - Settings           — auto-approve preference + unlink
 */
export default function ParentDashboard() {
  const { user, refresh } = useAuth();
  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(true);
  const [tab, setTab] = useState("overview");

  // Summary cache keyed by studentId → summary object
  const [summaries, setSummaries] = useState({});
  const [loadingSummaryFor, setLoadingSummaryFor] = useState(null);

  const [drawerItem, setDrawerItem] = useState(null); // {type, id, meta}
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actioning, setActioning] = useState(false);

  const kids = user?.linked_students || [];

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const { data } = await api.get("/parent/pending-approvals");
      setPending(data);
    } catch {
      setPending({ courses: [], enrollments: [] });
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadSummary = useCallback(async (studentId) => {
    setLoadingSummaryFor(studentId);
    try {
      const { data } = await api.get(`/parent/children/${studentId}/summary`);
      setSummaries((prev) => ({ ...prev, [studentId]: data }));
    } catch (err) {
      const detail = err?.response?.data?.detail || "load_failed";
      toast.error(`Couldn't load summary — ${detail}`);
    } finally {
      setLoadingSummaryFor(null);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    kids.forEach((c) => {
      if (!summaries[c.id] && loadingSummaryFor !== c.id) {
        loadSummary(c.id);
      }
    });
  }, [kids.map((c) => c.id).join(","), summaries, loadingSummaryFor, loadSummary, kids]);

  const pendingCount =
    (pending?.courses?.length || 0) + (pending?.enrollments?.length || 0);

  const openCourseDrawer = async (courseId, meta) => {
    setDrawerItem({ type: "course", id: courseId, meta });
    setDrawerData(null);
    setDrawerLoading(true);
    setRejectReason("");
    try {
      const { data } = await api.get(`/parent/courses/${courseId}`);
      setDrawerData(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "load_failed";
      toast.error(`Couldn't load course — ${detail}`);
      setDrawerItem(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const openEnrollmentDrawer = (enrollment) => {
    setDrawerItem({ type: "enrollment", id: enrollment.id, meta: enrollment });
    setDrawerData(enrollment);
    setDrawerLoading(false);
    setRejectReason("");
  };

  const closeDrawer = () => {
    setDrawerItem(null);
    setDrawerData(null);
    setRejectReason("");
  };

  const approveDrawer = async () => {
    if (!drawerItem) return;
    setActioning(true);
    try {
      if (drawerItem.type === "course") {
        await api.post(`/parent/courses/${drawerItem.id}/approve`);
      } else {
        await api.post(`/parent/enrollments/${drawerItem.id}/approve`);
      }
      toast.success("Approved. Your child has been notified.");
      closeDrawer();
      await loadPending();
      // Invalidate summaries so counts refresh next time the tab opens.
      setSummaries({});
    } catch (err) {
      const detail = err?.response?.data?.detail || "approve_failed";
      toast.error(`Approve failed — ${detail}`);
    } finally {
      setActioning(false);
    }
  };

  const rejectDrawer = async () => {
    if (!drawerItem) return;
    setActioning(true);
    try {
      if (drawerItem.type === "course") {
        await api.post(`/parent/courses/${drawerItem.id}/reject`, {
          rejection_reason: rejectReason.trim(),
        });
      } else {
        await api.post(`/parent/enrollments/${drawerItem.id}/reject`, {
          rejection_reason: rejectReason.trim(),
        });
      }
      toast.success("Rejected. Your child has been notified.");
      closeDrawer();
      await loadPending();
      setSummaries({});
    } catch (err) {
      const detail = err?.response?.data?.detail || "reject_failed";
      toast.error(`Reject failed — ${detail}`);
    } finally {
      setActioning(false);
    }
  };

  const setAutoApprove = async (studentId, value) => {
    try {
      await api.put(`/parent/students/${studentId}/preferences`, {
        auto_approve_self_paced: value,
      });
      toast.success(
        value
          ? "Auto-approve turned on for future courses."
          : "Auto-approve turned off. You'll review each new course."
      );
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "update_failed";
      toast.error(`Couldn't update — ${detail}`);
    }
  };

  const unlinkChild = async (studentId, name) => {
    if (
      !window.confirm(
        `Unlink ${name}? Their pending courses will remain pending until re-linked.`
      )
    )
      return;
    try {
      await api.delete(`/parent/students/${studentId}/link`);
      toast.success("Unlinked. Refreshing your account…");
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "unlink_failed";
      toast.error(`Unlink failed — ${detail}`);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="parent-dashboard"
    >
      <DashboardHeader subtitle="Parent · Guardian dashboard" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
        <HeroBanner name={user?.name} pendingCount={pendingCount} loading={loadingPending} onReview={() => setTab("pending")} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-stone-200">
            <TabsTrigger value="overview" data-testid="parent-tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="children" data-testid="parent-tab-children">
              Children
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="parent-tab-pending">
              Pending
              {pendingCount > 0 && (
                <Badge
                  className="ml-2 bg-amber-500 text-stone-900"
                  data-testid="parent-tab-pending-badge"
                >
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="progress" data-testid="parent-tab-progress">
              Progress
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="parent-tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="pt-4">
            <OverviewTab
              kids={kids}
              pending={pending}
              loadingPending={loadingPending}
              onReview={() => setTab("pending")}
              onOpenChildren={() => setTab("children")}
            />
          </TabsContent>

          <TabsContent value="children" className="pt-4">
            <ChildrenTab
              kids={kids}
              summaries={summaries}
              loadingSummaryFor={loadingSummaryFor}
              onOpenCourse={(courseId, meta) => openCourseDrawer(courseId, meta)}
            />
          </TabsContent>

          <TabsContent value="pending" className="pt-4">
            <PendingTab
              pending={pending}
              loading={loadingPending}
              onOpenCourse={openCourseDrawer}
              onOpenEnrollment={openEnrollmentDrawer}
            />
          </TabsContent>

          <TabsContent value="progress" className="pt-4">
            <ProgressTab kids={kids} summaries={summaries} />
          </TabsContent>

          <TabsContent value="settings" className="pt-4">
            <SettingsTab
              kids={kids}
              onToggle={setAutoApprove}
              onUnlink={unlinkChild}
            />
          </TabsContent>
        </Tabs>
      </main>

      <ApprovalDrawer
        item={drawerItem}
        data={drawerData}
        loading={drawerLoading}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onClose={closeDrawer}
        onApprove={approveDrawer}
        onReject={rejectDrawer}
        actioning={actioning}
      />
    </div>
  );
}

// ============================================================
// Sub-sections
// ============================================================

function HeroBanner({ name, pendingCount, loading, onReview }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-amber-700">
        <Shield className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">
          Family overview
        </span>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" data-testid="parent-welcome-heading">
            Welcome{name ? `, ${name}` : ""}
          </h1>
          <p className="text-sm text-stone-600 max-w-xl mt-1">
            Approve the learning paths your child creates, keep an eye on
            progress, and set preferences per child.
          </p>
        </div>
        {!loading && pendingCount > 0 && (
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={onReview}
            data-testid="parent-hero-review-button"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Review {pendingCount} pending
          </Button>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "stone", testid, hint }) {
  const tones = {
    stone: "border-stone-200",
    amber: "border-amber-200 bg-amber-50/40",
    green: "border-emerald-200 bg-emerald-50/40",
  };
  return (
    <Card className={tones[tone]} data-testid={testid}>
      <CardContent className="py-4">
        <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">
          {label}
        </div>
        <div className="text-3xl font-semibold">{value}</div>
        {hint && (
          <div className="text-xs text-stone-500 mt-1">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewTab({ kids, pending, loadingPending, onReview, onOpenChildren }) {
  const pendingCount =
    (pending?.courses?.length || 0) + (pending?.enrollments?.length || 0);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Linked children"
          value={kids.length}
          testid="parent-stat-children"
        />
        <StatCard
          label="Awaiting your review"
          value={loadingPending ? <Loader2 className="h-6 w-6 animate-spin" /> : pendingCount}
          tone="amber"
          testid="parent-stat-pending"
          hint={pendingCount > 0 ? "Approve or reject to unlock" : "You&apos;re all caught up"}
        />
        <StatCard
          label="Auto-approve enabled"
          value={
            kids.filter((c) => c.auto_approve_self_paced).length + " / " + kids.length
          }
          testid="parent-stat-auto"
          hint="Manage in Settings"
        />
      </div>

      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600 flex items-center gap-2">
            <Users className="h-4 w-4" /> Your children
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kids.length === 0 ? (
            <EmptyChildren />
          ) : (
            <ul className="space-y-3">
              {kids.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded-md border border-stone-200 p-3"
                  data-testid={`parent-overview-child-row-${c.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-stone-100 flex items-center justify-center">
                      <UserRound className="h-4 w-4 text-stone-500" />
                    </div>
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-stone-500">Age {c.age ?? "—"}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-stone-50">
                    {c.auto_approve_self_paced ? "Auto-approve on" : "Manual approval"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          {kids.length > 0 && (
            <div className="pt-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onOpenChildren}
                data-testid="parent-overview-view-children"
              >
                View child details
              </Button>
              {pendingCount > 0 && (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={onReview}
                  data-testid="parent-overview-review"
                >
                  Review pending ({pendingCount})
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmptyChildren() {
  return (
    <div className="text-sm text-stone-500 py-6 text-center">
      No linked children yet. Once a child sends you an invite from their KKS
      account, they&apos;ll appear here automatically.
    </div>
  );
}

function ChildrenTab({ kids, summaries, loadingSummaryFor, onOpenCourse }) {
  if (kids.length === 0) return <EmptyChildren />;
  return (
    <div className="space-y-6">
      {kids.map((c) => {
        const summary = summaries[c.id];
        return (
          <Card key={c.id} className="border-stone-200" data-testid={`parent-child-detail-${c.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <UserRound className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <div className="text-base font-semibold">{c.name}</div>
                    <div className="text-xs text-stone-500">
                      Age {c.age ?? "—"} · Auto-approve{" "}
                      {c.auto_approve_self_paced ? "on" : "off"}
                    </div>
                  </div>
                </div>
                {loadingSummaryFor === c.id && (
                  <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {summary ? (
                <ChildSummaryBody
                  summary={summary}
                  onOpenCourse={onOpenCourse}
                />
              ) : (
                <div className="text-xs text-stone-500">Loading…</div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ChildSummaryBody({ summary, onOpenCourse }) {
  const counts = summary.counts || {};
  const courses = summary.self_paced_courses || [];
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-4">
        <MiniStat label="Self-paced" value={counts.total_self_paced ?? 0} testid="mini-stat-self-paced" />
        <MiniStat label="Approved" value={counts.approved_courses ?? 0} tone="green" testid="mini-stat-approved" />
        <MiniStat label="Pending" value={counts.pending_courses ?? 0} tone="amber" testid="mini-stat-pending" />
        <MiniStat label="Rejected" value={counts.rejected_courses ?? 0} tone="stone" testid="mini-stat-rejected" />
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2 flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5" /> Self-paced courses
        </div>
        {courses.length === 0 ? (
          <div className="text-xs text-stone-500 py-3">No self-paced courses yet.</div>
        ) : (
          <ul className="space-y-2">
            {courses.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between rounded-md border border-stone-200 p-3 text-sm"
                data-testid={`child-course-row-${row.id}`}
              >
                <div className="min-w-0 pr-3">
                  <div className="font-medium truncate">{row.title}</div>
                  <div className="text-xs text-stone-500 truncate">
                    {row.level} · {row.chapter_count} chapter{row.chapter_count === 1 ? "" : "s"}
                    {row.sensitive_bucket && (
                      <>
                        {" "}·{" "}
                        <span className="text-amber-700">
                          {row.sensitive_bucket}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ApprovalBadge status={row.parent_approval?.status} />
                  {(row.parent_approval?.status === "pending" ||
                    row.parent_approval?.status === "approved" ||
                    row.parent_approval?.status === "rejected") && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onOpenCourse(row.id, row)}
                      data-testid={`child-course-view-${row.id}`}
                    >
                      View
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {summary.recent_activity?.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
            Recent activity
          </div>
          <ul className="space-y-1.5 text-xs text-stone-600">
            {summary.recent_activity.slice(0, 5).map((row) => (
              <li key={row.id} className="flex justify-between gap-3">
                <span className="truncate">{row.subject}</span>
                <span className="text-stone-400 shrink-0">
                  {formatDate(row.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function MiniStat({ label, value, tone = "stone", testid }) {
  const tones = {
    stone: "bg-stone-50 border-stone-200",
    amber: "bg-amber-50/60 border-amber-200",
    green: "bg-emerald-50/60 border-emerald-200",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-stone-500">
        {label}
      </div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

function ApprovalBadge({ status }) {
  if (!status || status === "not_required") {
    return (
      <Badge variant="outline" className="text-xs">
        —
      </Badge>
    );
  }
  const map = {
    pending: { cls: "bg-amber-100 text-amber-900 border-amber-200", label: "Pending" },
    approved: { cls: "bg-emerald-100 text-emerald-900 border-emerald-200", label: "Approved" },
    rejected: { cls: "bg-red-100 text-red-900 border-red-200", label: "Rejected" },
  };
  const it = map[status];
  return (
    <Badge className={`${it.cls} border text-xs`} data-testid={`approval-badge-${status}`}>
      {it.label}
    </Badge>
  );
}

function PendingTab({ pending, loading, onOpenCourse, onOpenEnrollment }) {
  if (loading) {
    return (
      <div className="py-10 flex justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
      </div>
    );
  }
  const courses = pending?.courses || [];
  const enrollments = pending?.enrollments || [];
  if (courses.length === 0 && enrollments.length === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40">
        <CardContent className="py-8 text-center space-y-2">
          <CheckCircle2 className="h-6 w-6 text-emerald-700 mx-auto" />
          <div className="text-sm font-medium text-emerald-900">
            All caught up — nothing awaiting your review.
          </div>
          <div className="text-xs text-emerald-800">
            You&apos;ll get an email whenever your child creates a new learning
            path.
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      {courses.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2 flex items-center gap-2">
            <ClipboardList className="h-3.5 w-3.5" /> Self-paced courses ({courses.length})
          </div>
          <ul className="space-y-2">
            {courses.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/40 p-3 text-sm"
                data-testid={`pending-course-row-${row.id}`}
              >
                <div className="min-w-0 pr-3">
                  <div className="font-medium truncate">{row.title}</div>
                  <div className="text-xs text-stone-600 truncate">
                    {row.level}
                    {row.learning_profile?.sensitive_bucket && (
                      <>
                        {" "}· <span className="text-amber-700 font-medium">
                          {row.learning_profile.sensitive_bucket}
                        </span>{" "}
                        (supportive framing)
                      </>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  onClick={() => onOpenCourse(row.id, row)}
                  data-testid={`pending-course-review-${row.id}`}
                >
                  Review
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {enrollments.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> Paid enrollments ({enrollments.length})
          </div>
          <ul className="space-y-2">
            {enrollments.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50/40 p-3 text-sm"
                data-testid={`pending-enrollment-row-${row.id}`}
              >
                <div className="min-w-0 pr-3">
                  <div className="font-medium truncate">
                    {row.course_title || "Course"}
                  </div>
                  <div className="text-xs text-stone-600">
                    {row.plan_chosen || "no plan"} · {row.currency || "—"}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  onClick={() => onOpenEnrollment(row)}
                  data-testid={`pending-enrollment-review-${row.id}`}
                >
                  Review
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ProgressTab({ kids, summaries }) {
  if (kids.length === 0) return <EmptyChildren />;
  return (
    <div className="space-y-4">
      {kids.map((c) => {
        const summary = summaries[c.id];
        const counts = summary?.counts || {};
        const total = counts.total_self_paced ?? 0;
        const approved = counts.approved_courses ?? 0;
        const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
        return (
          <Card key={c.id} className="border-stone-200" data-testid={`parent-progress-${c.id}`}>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-stone-500">
                    Age {c.age ?? "—"}
                  </div>
                </div>
                <div className="text-xs text-stone-500">
                  {approved} of {total} approved
                </div>
              </div>
              <Progress value={pct} className="h-2" />
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded border border-emerald-200 bg-emerald-50/50 px-2 py-1.5">
                  <div className="text-[10px] text-emerald-800">Approved</div>
                  <div className="font-semibold text-emerald-900">
                    {counts.approved_courses ?? 0}
                  </div>
                </div>
                <div className="rounded border border-amber-200 bg-amber-50/50 px-2 py-1.5">
                  <div className="text-[10px] text-amber-800">Pending</div>
                  <div className="font-semibold text-amber-900">
                    {counts.pending_courses ?? 0}
                  </div>
                </div>
                <div className="rounded border border-red-200 bg-red-50/50 px-2 py-1.5">
                  <div className="text-[10px] text-red-800">Rejected</div>
                  <div className="font-semibold text-red-900">
                    {counts.rejected_courses ?? 0}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SettingsTab({ kids, onToggle, onUnlink }) {
  if (kids.length === 0) return <EmptyChildren />;
  return (
    <div className="space-y-4">
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-stone-600 flex items-center gap-2">
            <Settings2 className="h-4 w-4" /> Per-child preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {kids.map((c) => (
            <div
              key={c.id}
              className="rounded-md border border-stone-200 p-4 space-y-3"
              data-testid={`parent-settings-row-${c.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-stone-500">
                    Age {c.age ?? "—"}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => onUnlink(c.id, c.name)}
                  data-testid={`parent-settings-unlink-${c.id}`}
                >
                  <XCircle className="mr-1 h-3.5 w-3.5" /> Unlink
                </Button>
              </div>
              <div className="flex items-start justify-between gap-3 rounded-md bg-stone-50 p-3">
                <div>
                  <Label
                    htmlFor={`auto-${c.id}`}
                    className="text-sm font-medium"
                  >
                    Auto-approve self-paced courses
                  </Label>
                  <div className="text-xs text-stone-500 mt-0.5">
                    New AI-generated self-paced courses will be approved
                    automatically. You&apos;ll still see them here.
                  </div>
                </div>
                <Switch
                  id={`auto-${c.id}`}
                  checked={!!c.auto_approve_self_paced}
                  onCheckedChange={(v) => onToggle(c.id, v)}
                  data-testid={`parent-settings-auto-${c.id}`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// Drawer for course/enrollment approval
// ============================================================

function ApprovalDrawer({
  item,
  data,
  loading,
  rejectReason,
  setRejectReason,
  onClose,
  onApprove,
  onReject,
  actioning,
}) {
  const open = !!item;
  const status = data?.parent_approval?.status;
  const canAct = status === "pending";
  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        data-testid="parent-approval-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-base">
            {loading ? "Loading…" : data?.title || "Review"}
          </SheetTitle>
          <SheetDescription>
            {item?.type === "course"
              ? "Full course content — read every chapter before deciding."
              : "Course enrollment request — approve to let payment continue."}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          </div>
        )}

        {!loading && data && item?.type === "course" && (
          <CourseDrawerBody data={data} />
        )}

        {!loading && data && item?.type === "enrollment" && (
          <EnrollmentDrawerBody data={data} />
        )}

        {canAct && (
          <div className="mt-6 space-y-3 border-t border-stone-200 pt-4">
            <Label htmlFor="reject-reason" className="text-sm">
              Rejection reason (optional — shown to your child)
            </Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={2}
              placeholder="E.g., let's discuss this topic together first."
              data-testid="parent-drawer-reject-reason"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={onReject}
                disabled={actioning}
                data-testid="parent-drawer-reject-button"
              >
                {actioning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="mr-1 h-4 w-4" /> Reject
                  </>
                )}
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={onApprove}
                disabled={actioning}
                data-testid="parent-drawer-approve-button"
              >
                {actioning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {!canAct && status && (
          <div
            className={`mt-6 rounded-md border p-3 text-sm ${
              status === "approved"
                ? "border-emerald-200 bg-emerald-50/60 text-emerald-900"
                : "border-red-200 bg-red-50/60 text-red-900"
            }`}
            data-testid="parent-drawer-already-reviewed"
          >
            {status === "approved" ? (
              <>Already approved.</>
            ) : (
              <>Already rejected.</>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CourseDrawerBody({ data }) {
  const profile = data.learning_profile || {};
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-stone-200 bg-stone-50 p-3 text-xs space-y-1">
        <div>
          <span className="text-stone-500">Level:</span> {data.level}
        </div>
        <div>
          <span className="text-stone-500">Age bucket:</span>{" "}
          {profile.age_bucket || "—"} (age {profile.age_years || "—"})
        </div>
        {profile.sensitive_bucket && (
          <div className="flex items-center gap-1.5 text-amber-800">
            <HeartPulse className="h-3.5 w-3.5" />
            Sensitive topic: {profile.sensitive_bucket}
            {profile.supportive_framing && " · supportive framing applied"}
          </div>
        )}
      </div>

      <div className="text-sm text-stone-700">{data.description}</div>

      <div className="space-y-3">
        {(data.chapters || []).map((ch, i) => (
          <div
            key={ch.id || i}
            className="rounded-md border border-stone-200 p-3"
            data-testid={`parent-drawer-chapter-${i}`}
          >
            <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">
              Chapter {i + 1}
            </div>
            <div className="text-sm font-semibold mb-2">{ch.title}</div>
            {ch.classwork?.lesson_notes_markdown && (
              <details className="text-xs text-stone-700">
                <summary className="cursor-pointer text-stone-500 py-1">
                  Lesson notes preview
                </summary>
                <div className="mt-1 whitespace-pre-wrap max-h-48 overflow-y-auto pr-2 border-l-2 border-stone-200 pl-2">
                  {ch.classwork.lesson_notes_markdown.slice(0, 800)}
                  {ch.classwork.lesson_notes_markdown.length > 800 && "…"}
                </div>
              </details>
            )}
            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px] text-stone-500">
              <div>
                Examples: <strong>{(ch.classwork?.examples || []).length}</strong>
              </div>
              <div>
                MCQs: <strong>{(ch.test?.mcq || []).length}</strong>
              </div>
              <div>
                Homework Qs:{" "}
                <strong>{(ch.homework?.questions || []).length}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EnrollmentDrawerBody({ data }) {
  return (
    <div className="mt-4 space-y-3 text-sm">
      <div className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-1 text-xs">
        <div>
          <span className="text-stone-500">Course:</span>{" "}
          {data.course_title || "—"}
        </div>
        <div>
          <span className="text-stone-500">Plan:</span>{" "}
          {data.plan_chosen || "—"}
        </div>
        <div>
          <span className="text-stone-500">Currency:</span>{" "}
          {data.currency || "—"}
        </div>
        <div>
          <span className="text-stone-500">Status:</span> {data.status}
        </div>
      </div>
      <div className="text-xs text-stone-500 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
        Approving lets your child continue to payment. You can still monitor
        progress after approval.
      </div>
    </div>
  );
}

// ============================================================
// Utils
// ============================================================

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}
